import { useContext, useMemo } from "react";
import { Layout, Menu, Dropdown, Tooltip } from "antd";
import type { MenuProps } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppstoreOutlined, TeamOutlined, ClockCircleOutlined, BarChartOutlined,
  CustomerServiceOutlined, SettingOutlined, HomeOutlined, MenuFoldOutlined,
  MenuUnfoldOutlined, BulbOutlined, LockOutlined, LogoutOutlined,
} from "@ant-design/icons";
import AuthContext from "../../context/AuthContext";
import { Logo } from "../../components/ui";
import { useThemeMode } from "../../context/ThemeModeContext";
import { routes } from "../routes/routes";
import styles from "./AppSidebar.module.css";

const { Sider } = Layout;

interface Props {
  collapsed: boolean;
  onCollapse: (v: boolean) => void;
  mobile?: boolean;
  onNavigate?: () => void;
}

/**
 * Persistent left nav. Replaces the old 18vh centred logo header: it carries
 * the login screen's gradient into the authenticated app, and it filters its
 * own items by role rather than each page guarding itself.
 */
export const AppSidebar = ({ collapsed, onCollapse, mobile = false, onNavigate }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, onLogout, openCloseModalPassword, isAdmin } = useContext(AuthContext);
  const { mode, toggle } = useThemeMode();

  const role = userData?.role;
  const isStaff = role === "admin" || role === "user";

  const items: MenuProps["items"] = useMemo(() => {
    const isTruthy = <T,>(item: T | false): item is T => Boolean(item);

    const work = [
      { key: routes.dashboard, icon: <HomeOutlined />, label: "Home" },
      isStaff && { key: routes.projects, icon: <AppstoreOutlined />, label: "Projects" },
      isStaff && { key: routes.myTime, icon: <ClockCircleOutlined />, label: "My Time" },
      { key: routes.tickets, icon: <CustomerServiceOutlined />, label: "Support" },
    ].filter(isTruthy);

    const manage = [
      isStaff && { key: routes.clients, icon: <TeamOutlined />, label: "Clients" },
      isAdmin && { key: routes.reports, icon: <BarChartOutlined />, label: "Reports" },
      isAdmin && { key: routes.adminUsers, icon: <SettingOutlined />, label: "Users" },
    ].filter(isTruthy);

    return manage.length
      ? [...work, { type: "divider" as const, style: { margin: "12px 4px", borderColor: "rgba(255,255,255,.12)" } }, ...manage]
      : work;
  }, [isStaff, isAdmin]);

  // longest matching route wins, so /clients/:id keeps Clients lit
  const selected = useMemo(() => {
    const keys = (items || [])
      .map((i) => (i && "key" in i ? String(i.key) : ""))
      .filter((k) => k && (k === "/" ? location.pathname === "/" : location.pathname.startsWith(k)));
    return keys.sort((a, b) => b.length - a.length).slice(0, 1);
  }, [items, location.pathname]);

  const userMenu: MenuProps["items"] = [
    { key: "password", label: "Change password", icon: <LockOutlined />, onClick: () => openCloseModalPassword(true) },
    { key: "theme", label: mode === "light" ? "Dark theme" : "Light theme", icon: <BulbOutlined />, onClick: toggle },
    { type: "divider" },
    { key: "logout", label: "Log out", icon: <LogoutOutlined />, danger: true, onClick: () => onLogout() },
  ];

  const initials = (userData?.name || "?")
    .split(" ").slice(0, 2).map((w: string) => w[0]).join("").toUpperCase();

  const navigateTo = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  return (
    <Sider
      collapsible={!mobile}
      collapsed={mobile ? false : collapsed}
      onCollapse={onCollapse}
      trigger={null}
      width={mobile ? 280 : 224}
      collapsedWidth={56}
      className={`${styles.sider} ${mobile ? styles.mobileSider : ""}`}
      theme="dark"
    >
      <button className={styles.brand} type="button" onClick={() => navigateTo(routes.dashboard)} aria-label="Go to dashboard">
        <span className={`${styles.plate} ${collapsed ? styles.plateCollapsed : ""}`}>
          <Logo mobile={mobile ? false : collapsed} />
        </span>
      </button>

      <div className={styles.scroll}>
        <Menu
          theme="dark"
          mode="inline"
          items={items}
          selectedKeys={selected}
          onClick={({ key }) => navigateTo(key)}
          style={{ background: "transparent", borderInlineEnd: 0 }}
        />
      </div>

      <div className={styles.foot}>
        <Dropdown menu={{ items: userMenu, inlineCollapsed: false }} trigger={["click"]} placement="topLeft">
          <button className={styles.user} type="button" aria-label="Open account menu">
            <span className={styles.avatar}>{initials}</span>
            {!collapsed && (
              <span className={styles.userMeta}>
                <span className={styles.userName}>{userData?.name}</span>
                <span className={styles.userRole}>{role === "final_user" ? "Client" : role}</span>
              </span>
            )}
          </button>
        </Dropdown>
        <Tooltip title={collapsed ? "Expand" : "Collapse"} placement="right">
          <button className={styles.user} type="button" aria-label={collapsed ? "Expand navigation" : "Collapse navigation"} onClick={() => onCollapse(!collapsed)}>
            <span className={styles.avatar} style={{ background: "transparent" }}>
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </span>
          </button>
        </Tooltip>
      </div>
    </Sider>
  );
};
